<template>
  <v-card>

    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn @click="$emit('clone', node)" flat small color="primary"><v-icon>add</v-icon> clone</v-btn>
      <v-btn @click="$emit('edit', node)" flat small color="primary"><v-icon>edit</v-icon> edit</v-btn>
      <v-btn @click="$emit('delete', node)" flat small color="primary"><v-icon>delete</v-icon> delete</v-btn>
    </v-card-actions>

    <v-card-title primary-title>
      <h2>Channel collection "{{ node.label }}"</h2>
      <span class="node-id">#{{ node.id }}</span>
    </v-card-title>

    <v-card-text>

      <div>
        <v-card-title><h4>Global parameters</h4></v-card-title>
        <table class="display-node">
          <tbody>
            <tr><th>Network</th><td>{{ nodeMapping.network[node.data.network].label }}</td></tr>
            <tr><th>Location</th><td>{{ nodeMapping.location[node.data.location].label }}</td></tr>
            <tr><th>Channel flags</th><td>{{ node.data.flags.join(', ') }}</td></tr>
            <tr><th>Format</th><td>{{ node.data.format }}</td></tr>
            <tr><th>Open date</th><td>{{ node.data.open }}</td></tr>
            <tr><th>Close date</th><td>{{ node.data.close }}</td></tr>
          </tbody>
        </table>
      </div>

      <div>
        <v-card-title><h4>Devices</h4></v-card-title>
        <table class="display-node">
          <thead>
            <tr>
              <th>Device type</th>
              <th>Manufacturer</th>
              <th>Model</th>
              <th>Serial number</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in devicesTableStruct">
              <td v-for="value in row">{{ value }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div>
        <v-card-title><h4>Channels</h4></v-card-title>
        <table class="display-node">
          <thead>
            <tr v-for="header in channelsTableStruct.header">
              <th v-for="h in header" v-bind="h.attrs">{{ h.text }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in channelsTableStruct.rows">
              <template v-for="col in row">
                <td v-if="col.list">
                  <ul>
                    <li v-for="item in col.items">{{ item }}</li>
                  </ul>
                </td>
                <td v-else>{{ col.text }}</td>
              </template>
            </tr>
          </tbody>
        </table>
      </div>

      <div>
        <v-card-title><h4>Plot response</h4></v-card-title>
        <v-btn :disabled="loading || plotValues != null" @click="handlePlotResponseBtnClick">
          {{ loading ? 'Loading...' : 'Plot' }}
        </v-btn>
        <div class="plot_response--gain"></div>
        <div class="plot_response--phase"></div>
      </div>

    </v-card-text>

  </v-card>
</template>


<script>
import Highcharts from 'highcharts'

const DEVICE_STRUCT = [
  {
    multiple: false,
    key: 'sensor',
    label: 'Sensor',
    properties: [
      { key: 'component', label: 'Component' },
      { key: 'configuration', label: 'Configuration' }
    ]
  },
  {
    multiple: true,
    label: 'Analog filter',
    key: 'analog_filter',
    properties: [
      { key: 'component', label: 'Component' }
    ]
  },
  {
    multiple: false,
    label: 'Digitizer',
    key: 'digitizer',
    properties: [
      { key: 'component', label: 'Component' },
      { key: 'analog_filter', label: 'Analog filter' },
      { key: 'digital_filter', label: 'Sampling frequency' },
      { key: 'configuration', label: 'Configuration' }
    ]
  },
  {
    multiple: true,
    label: 'Digital filter',
    key: 'digital_filter',
    properties: [
      { key: 'filter', label: 'Decimation' }
    ]
  }
]

export default {
  name: 'display-channel-collection',

  props: ['node', 'nodeMapping', 'plotValues'],

  data () {
    return {
      loading: false,
      devicesTableStruct: this.getDevicesTableStruct(),
      channelsTableStruct: this.getChannelsTableStruct()
    }
  },

  watch: {
    plotValues: function(newValue, oldValue) {
      if (newValue != null) {
        this.plotResponse()
      }
    },
    node: function(newValue, oldValue) {
      this.loading = false
      this.devicesTableStruct = this.getDevicesTableStruct()
      this.channelsTableStruct = this.getChannelsTableStruct()
      this.$el.querySelector('.plot_response--gain').innerHTML = ''
      this.$el.querySelector('.plot_response--phase').innerHTML = ''
    }
  },

  methods: {

    getDevicesTableStruct () {
      let result = []
      for (let dev of DEVICE_STRUCT) {
        let devObj = this.node.data.device[dev.key]
        if (devObj == null) {
          continue
        }
        if (dev.multiple) {
          for (let d of devObj) {
            result.push([dev.label, d[0], d[1], dev.key != 'digital_filter' ? d[2] : ''])
          }
        } else {
          result.push([dev.label, devObj[0], devObj[1], devObj[2]])
        }
      }
      return result
    },

    getChannelsTableStruct () {
      let h_content_1 = []
      let h_content_2 = []
      let dev_values = this.node.data.channel.map(ch => [])
      for (let dev of DEVICE_STRUCT) {
        let devProp = this.node.data.channel[0][dev.key]
        if (devProp != null) {
          let colspan = 0
          for (let prop of dev.properties) {
            if (dev.multiple) {
              if (devProp[0][prop.key] != null) {
                colspan++
                h_content_2.push({ attrs: {}, text: prop.label })
                for (let [i, ch] of this.node.data.channel.entries()) {
                  let devPropContent = []
                  for (let currDev of Object.values(ch[dev.key])) {
                    devPropContent.push(currDev[prop.key])
                  }
                  dev_values[i].push({ list: true, items: devPropContent })
                }
              }
            } else {
              if (devProp[prop.key] != null) {
                colspan++
                h_content_2.push({ attrs: {}, text: prop.label })
                for (let [i, ch] of this.node.data.channel.entries()) {
                  dev_values[i].push({ text: ch[dev.key][prop.key] })
                }
              }
            }
          }
          h_content_1.push({ attrs: { colspan }, text: dev.label })
        }
      }
      let softwareFilter = this.node.data.channel[0].software_filter
      if (softwareFilter != null && softwareFilter != '') {
        h_content_1.push({ attrs: { rowspan: 2 }, text: 'Software filter' })
        for (let [i, ch] of this.node.data.channel.entries()) {
          dev_values[i].push(ch.software_filter)
        }
      }
      return {
        header: [
          [
            { attrs: { rowspan: 2 }, text: 'Code' }
          ].concat(h_content_1).concat([
            { attrs: { rowspan: 2 }, text: 'Azimuth' },
            { attrs: { rowspan: 2 }, text: 'Dip' },
            { attrs: { rowspan: 2 }, text: 'Alternate Code' },
            { attrs: { rowspan: 2 }, text: 'Historical Code' },
            { attrs: { rowspan: 2 }, text: 'Restricted Status' }
          ]),
          h_content_2
        ],
        rows: this.node.data.channel.map((ch, i) => [
          { text: ch.iris_code }
        ].concat(dev_values[i]).concat([
          { text: ch.orientation.azimuth },
          { text: ch.orientation.dip },
          { text: ch.alternate },
          { text: ch.historical },
          { text: ch.restricted }
        ]))
      }
    },

    plotResponse () {
      this.loading = false
      let gainContainer = this.$el.querySelector('.plot_response--gain'),
          phaseContainer = this.$el.querySelector('.plot_response--phase'),
          gainChartOpt = { title: { text: 'Gain' }, xAxis: { type: 'logarithmic' }, yAxis: { type: 'logarithmic', title: { text: 'Amplitude' } }, series: [] },
          phaseChartOpt = { title: { text: 'Phase' }, xAxis: { type: 'logarithmic' }, yAxis: { title: { text: 'Phase [rad]' } }, series: [] }
      for (let [k, v] of Object.entries(this.plotValues.gain)) {
        gainChartOpt.series.push({ name: k, data: v })
      }
      for (let [k, v] of Object.entries(this.plotValues.phase)) {
        phaseChartOpt.series.push({ name: k, data: v })
      }
      Highcharts.chart(gainContainer, gainChartOpt)
      Highcharts.chart(phaseContainer, phaseChartOpt)
    },

    handlePlotResponseBtnClick () {
      this.loading = true
      this.$emit('plotResponse', this.node)
    }

  }
}
</script>
